import { isEqual } from 'lodash'

import { REQUEST_ENTITY_KEYS } from '@/utils/entity-keys'
import { DEFAULT_RESULT_TYPE_OPTIONS, KR_VALUE_TYPES } from '@/utils/metrics-helpers'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

export const THRESHOLD_CONDITIONS = {
  BELOW: {
    label: 'common.below',
    value: 3,
    symbol: MATH_SYMBOLS.LESS_THAN
  },
  ABOVE: {
    label: 'common.above',
    value: 1,
    symbol: MATH_SYMBOLS.GREATER_THAN
  },
  BELOW_OR_EQUAL: {
    label: 'common.below_or_equal',
    value: 4, // default value
    symbol: MATH_SYMBOLS.LESS_THAN_OR_EQUAL
  },
  ABOVE_OR_EQUAL: {
    label: 'common.above_or_equal',
    value: 2,
    symbol: MATH_SYMBOLS.GREATER_THAN_OR_EQUAL
  }
}

export const METRIC_VALUES_KEYS = {
  BINARY_VALUE: 'binaryValue',
  PERCENT_ORIGINAL_VALUE: 'percentOriginalValue',
  PERCENT_CURRENT_VALUE: 'percentCurrentValue',
  PERCENT_TARGET_VALUE: 'percentTargetValue',
  NUMBER_ORIGINAL_VALUE: 'numberOriginalValue',
  NUMBER_CURRENT_VALUE: 'numberCurrentValue',
  NUMBER_TARGET_VALUE: 'numberTargetValue',
  THRESHOLD_ORIGINAL_VALUE: 'thresholdOriginalValue',
  THRESHOLD_CURRENT_VALUE: 'thresholdCurrentValue',
  THRESHOLD_TARGET_VALUE: 'thresholdTargetValue',
  ORIGINAL_VALUE: 'originalValue',
  CURRENT_VALUE: 'currentValue',
  TARGET_VALUE: 'targetValue',
  THRESHOLD_CONDITION_TYPE_ID: 'thresholdConditionTypeId'
}

const {
  ORIGINAL_VALUE,
  CURRENT_VALUE,
  TARGET_VALUE,
  THRESHOLD_ORIGINAL_VALUE,
  THRESHOLD_CURRENT_VALUE,
  THRESHOLD_TARGET_VALUE,
  NUMBER_TARGET_VALUE,
  NUMBER_ORIGINAL_VALUE,
  NUMBER_CURRENT_VALUE,
  BINARY_VALUE
} = METRIC_VALUES_KEYS

const { FIELD_TYPE_ID } = REQUEST_ENTITY_KEYS

const findConditionBiId = ({ conditionId = null } = {}) => {
  if (!conditionId) return undefined

  return Object.values(THRESHOLD_CONDITIONS).find(condition => condition.value === conditionId)
}

export const getThresholdConditionSymbol = ({ conditionId = null } = {}) => {
  if (!conditionId) return ''

  return findConditionBiId({ conditionId })?.symbol || ''
}

export const getConditionParameterById = ({ conditionId = null, parameter = 'label' }) => {
  if (!conditionId || !parameter) return undefined

  const conditionInfo = findConditionBiId({ conditionId })

  if (!conditionInfo || !conditionInfo[parameter]) return undefined

  return conditionInfo[parameter]
}

export const isBinaryProgress = ({ element }) => {
  return element[FIELD_TYPE_ID] === KR_VALUE_TYPES.BINARY
}

export const isThresholdProgress = ({ element }) => {
  return element[FIELD_TYPE_ID] === KR_VALUE_TYPES.THRESHOLD
}

export const isStartTargetProgress = ({ element }) => {
  return element[FIELD_TYPE_ID] === KR_VALUE_TYPES.START_TARGET
}

export const getTargetValue = element => {
  if (isStartTargetProgress({ element })) {
    return element[NUMBER_TARGET_VALUE]
  }

  if (isThresholdProgress({ element })) {
    return element[THRESHOLD_TARGET_VALUE]
  }

  return element[TARGET_VALUE]
}

export const isBinarySelectDisabled = modelValue => {
  return (
    modelValue &&
    modelValue[ORIGINAL_VALUE] === modelValue[CURRENT_VALUE] &&
    modelValue[ORIGINAL_VALUE] === 1
  )
}

export const getOriginalValue = element => {
  if (isStartTargetProgress({ element })) {
    return element[NUMBER_ORIGINAL_VALUE]
  }

  if (isThresholdProgress({ element })) {
    return element[THRESHOLD_ORIGINAL_VALUE]
  }

  return element[ORIGINAL_VALUE]
}

export const getCurrentValue = element => {
  const currentValueByFiledTypeId = {
    [KR_VALUE_TYPES.THRESHOLD]: element[THRESHOLD_CURRENT_VALUE],
    [KR_VALUE_TYPES.START_TARGET]: element[NUMBER_CURRENT_VALUE],
    [KR_VALUE_TYPES.BINARY]: element[BINARY_VALUE]
  }

  return currentValueByFiledTypeId[element[FIELD_TYPE_ID]]
}

export const valueToStringOrNull = value => (value !== null ? value.toString() : null)

export const GRADE_MODES = {
  AUTO: 1,
  MANUAL: 2
}

export const AUTO_GRADE_OPTION_VALUE = 4 // SEEMS ITS ONLY FE CUSTOM ID

export const JIRA_ISSUE_AUTO_GRADE_OPTION = {
  icon: 'jira-icon',
  label: 'jira.auto_grade',
  value: AUTO_GRADE_OPTION_VALUE
}

export const AZURE_WORK_ITEM_AUTO_GRADE_OPTION = {
  icon: 'azure-icon',
  label: 'jira.auto_grade',
  value: AUTO_GRADE_OPTION_VALUE
}

export const getProgressOptionByFieldTypeId = (id = null) => {
  return (
    DEFAULT_RESULT_TYPE_OPTIONS.find(({ value }) => value === id) ||
    getProgressOptionByFieldTypeId(KR_VALUE_TYPES.START_TARGET)
  )
}

export const THRESHOLD_PROGRESS_AGGREGATIONS = {
  LAST: {
    value: 1,
    progressDescriptionTitle: 'element_progress_aggregation_values[0]',
    progressDescriptionTriggerContent: 'okr_element.progress_description.last.trigger_text',
    progressModuleTooltip: 'progress_module_tooltip[0]',
    trackingLabel: 'last'
  },
  AVERAGE: {
    value: 2,
    progressDescriptionTitle: 'element_progress_aggregation_values[1]',
    progressDescriptionTriggerContent: 'okr_element.progress_description.avg.trigger_text',
    progressModuleTooltip: 'progress_module_tooltip[1]',
    trackingLabel: 'avg'
  },
  MIN: {
    value: 3,
    progressDescriptionTitle: 'element_progress_aggregation_values[2]',
    progressDescriptionTriggerContent: 'okr_element.progress_description.min.trigger_text',
    progressModuleTooltip: 'progress_module_tooltip[2]',
    trackingLabel: 'min'
  }
}

export const DEFAULT_THRESHOLD_PROGRESS_AGGREGATION = THRESHOLD_PROGRESS_AGGREGATIONS.AVERAGE.value

export const findProgressAggregationOptionsByTypeId = ({
  typeId = THRESHOLD_PROGRESS_AGGREGATIONS.LAST.value
} = {}) => {
  return (
    Object.values(THRESHOLD_PROGRESS_AGGREGATIONS).find(
      aggregation => aggregation.value === typeId
    ) || THRESHOLD_PROGRESS_AGGREGATIONS.LAST
  )
}

export const THRESHOLD_PERIOD_GRANULATION = {
  WEEK: {
    value: 1,
    label: 'units.week'
  },
  MONTH: {
    value: 2,
    label: 'units.month'
  },
  QUARTER: {
    value: 3,
    label: 'units.quarter'
  }
}

export const DEFAULT_THRESHOLD_PERIOD_GRANULATION = THRESHOLD_PERIOD_GRANULATION.MONTH.value

export const findPeriodGranulationOptionsByTypeId = ({
  typeId = DEFAULT_THRESHOLD_PERIOD_GRANULATION
}) => {
  return (
    Object.values(THRESHOLD_PERIOD_GRANULATION).find(granulation => granulation.value === typeId) ||
    THRESHOLD_PERIOD_GRANULATION.MONTH
  )
}

export const DEFAULT_BINARY_TARGET_VALUE = 1
export const DEFAULT_BINARY_ORIGINAL_VALUE = 0
export const DEFAULT_TARGET_VALUE = 100
export const DEFAULT_ORIGINAL_VALUE = 0
export const DEFAULT_CURRENT_VALUE = 0
export const DEFAULT_THRESHOLD_CURRENT_VALUE = null

const DEFAULT_CURRENT_VALUE_AS_STRING = `${DEFAULT_CURRENT_VALUE}` // 0
const DEFAULT_ORIGINAL_VALUE_AS_STRING = `${DEFAULT_ORIGINAL_VALUE}` // 0
const DEFAULT_TARGET_VALUE_AS_STRING = `${DEFAULT_TARGET_VALUE}` // 100

export const DEFAULT_RESULT_TYPE_VALUES = {
  fieldTypeId: null,
  [METRIC_VALUES_KEYS.BINARY_VALUE]: DEFAULT_BINARY_ORIGINAL_VALUE,
  [METRIC_VALUES_KEYS.PERCENT_ORIGINAL_VALUE]: DEFAULT_ORIGINAL_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.PERCENT_CURRENT_VALUE]: DEFAULT_CURRENT_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.PERCENT_TARGET_VALUE]: DEFAULT_TARGET_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.NUMBER_ORIGINAL_VALUE]: DEFAULT_ORIGINAL_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.NUMBER_CURRENT_VALUE]: DEFAULT_CURRENT_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.NUMBER_TARGET_VALUE]: DEFAULT_TARGET_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.THRESHOLD_ORIGINAL_VALUE]: DEFAULT_ORIGINAL_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.THRESHOLD_CURRENT_VALUE]: DEFAULT_THRESHOLD_CURRENT_VALUE,
  [METRIC_VALUES_KEYS.THRESHOLD_TARGET_VALUE]: DEFAULT_TARGET_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.ORIGINAL_VALUE]: DEFAULT_ORIGINAL_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.CURRENT_VALUE]: DEFAULT_CURRENT_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.TARGET_VALUE]: DEFAULT_TARGET_VALUE_AS_STRING,
  [METRIC_VALUES_KEYS.THRESHOLD_CONDITION_TYPE_ID]: THRESHOLD_CONDITIONS.ABOVE_OR_EQUAL.value,
  [OKR_ELEMENT_KEYS.UNIT]: ''
}

export const DEFAULT_MILESTONES_SETTINGS = {
  [OKR_ELEMENT_KEYS.ELEMENT_TARGET_PERIODS]: [],
  [OKR_ELEMENT_KEYS.PERIOD_AGGREGATION_TYPE_ID]: 1, // HARDCODED BECAUSE OF BE WANTS IT
  [OKR_ELEMENT_KEYS.PROGRESS_AGGREGATION_TYPE_ID]: 0,
  [OKR_ELEMENT_KEYS.GRANULATION_ID]: 0
}

export const EMPTY_MILESTONES_SETTINGS = {}

export const getResultTypeValues = (element = {}) => {
  const { originalValue, currentValue, targetValue } = element

  if (isBinaryProgress({ element })) {
    return {
      [BINARY_VALUE]: currentValue
    }
  }

  const normalizedOriginalValue = valueToStringOrNull(originalValue)
  const normalizedCurrentValue = valueToStringOrNull(currentValue)
  const normalizedTargetValue = valueToStringOrNull(targetValue)

  if (isStartTargetProgress({ element })) {
    return {
      [NUMBER_ORIGINAL_VALUE]: normalizedOriginalValue,
      [NUMBER_CURRENT_VALUE]: normalizedCurrentValue,
      [NUMBER_TARGET_VALUE]: normalizedTargetValue
    }
  }

  if (isThresholdProgress({ element })) {
    return {
      [THRESHOLD_ORIGINAL_VALUE]: normalizedOriginalValue,
      [THRESHOLD_CURRENT_VALUE]: normalizedCurrentValue,
      [THRESHOLD_TARGET_VALUE]: normalizedTargetValue
    }
  }

  return {}
}

export const notEqualStringifiedValues = (value1, value2) => !isEqual(`${value1}`, `${value2}`)

export const getResultTypeSettingsDisabledStates = ({
  isEdit,
  editable,
  isLockedOnPercent,
  isBinarySelectDisabled,
  valuesAreReadonly
}) => {
  const mainCondition = isEdit && !editable
  const inputCondition = valuesAreReadonly || mainCondition
  return {
    select: isLockedOnPercent || mainCondition,
    isSwitch: isBinarySelectDisabled || inputCondition,
    input: inputCondition
  }
}
